import transakSDK from '@transak/transak-sdk';
import { computed, ref, watch } from 'vue';
import { useWalletPlugin } from '@/composables/useWalletPlugin';
import { ChainsEnum, CORRECT_CHAIN, TRANSAK_API_KEY } from '@/constants';
import { EMixpanelEventNames } from '@/types/enums';
import moonpaySvg from '@/assets/payment/moonpay.svg';
import transakSvg from '@/assets/payment/transak.svg';
import coinbaseSvg from '@/assets/payment/coinbase.svg';
import { getMoonPayLink } from '@/composables/getMoonPayLink';
import { getCoinbaseLink } from '@/composables/getCoinbaseLink';

const { account } = useWalletPlugin();

const walletAddress = computed(() => account.value?.address);

/**
 * Buy APT menu items type description
 */
export type TPaymentBrand = 'moonpay' | 'transak' | 'coinbase';

type TPaymentOption = {
  type: TPaymentBrand;
  title: string;
  description: string;
  url: string;
  targetImg?: string;
  canBeShowed: boolean;
  fee?: string;
};

/**
 * Buy APT menu items
 */
const buyAptMenuItemsRaw = ref<TPaymentOption[]>([
  {
    type: 'moonpay',
    title: 'MoonPay',
    description: 'Card, Apple, Google Pay or bank transfers',
    url: '',
    targetImg: moonpaySvg,
    canBeShowed: false,
    fee: 'From 3.5%',
  },
  {
    type: 'transak',
    title: 'Transak',
    description: 'Card, Apple Pay, MobiKwik or bank transfers ',
    url: '',
    targetImg: transakSvg,
    canBeShowed: false,
    fee: 'From 3.5%',
  },
  {
    type: 'coinbase',
    title: 'CoinBase Pay',
    description: 'Buy or transfer crypto from Coinbase ',
    url: '',
    targetImg: coinbaseSvg,
    canBeShowed: false,
    fee: 'From 3.5%',
  },
]);

export const handlePurchaseByTransak = (walletAddress: string, track?: any) => {
  try {
    if (!walletAddress) return;

    const transakSettings = {
      apiKey: TRANSAK_API_KEY,
      environment: 'PRODUCTION', // STAGING/PRODUCTION
      widgetHeight: '600px',
      widgetWidth: '360px',
      defaultCryptoCurrency: 'APT', // Example 'ETH'
      cryptoCurrencyCode: 'APT',
      walletAddress, // Your customer's wallet address
      themeColor: '6f45c9', // App theme color
      fiatCurrency: '', // If you want to limit fiat selection eg 'GBP'
    };

    const transak = new transakSDK(transakSettings);
    transak.init();

    // This will trigger when the user marks payment is made
    transak.on(transak.EVENTS.TRANSAK_ORDER_SUCCESSFUL, () => {
      if (track) track(EMixpanelEventNames.FinishTransak);

      transak.close();
    });
  } catch (e) {
    console.error(e);
  }
};

/**
 * Should I show buy APT menu
 */
export const haveBuyAptUrls = computed(
  () =>
    ![
      ChainsEnum['lumio-testnet'],
      ChainsEnum['movement-testnet'],
      ChainsEnum['movement-mainnet'],
    ].includes(CORRECT_CHAIN.id) &&
    buyAptMenuItemsRaw.value.reduce(
      (prev: boolean, item: TPaymentOption) => prev || item.canBeShowed,
      false,
    ),
);

/**
 * Buy APT menu items with URLs filled
 */
export const buyAptMenuItems = computed(() =>
  buyAptMenuItemsRaw.value.filter((item) => item.canBeShowed),
);

/**
 * Fill URLs in buy APT Menu items
 */
export const fillBuyAptMenuItems = () => {
  buyAptMenuItemsRaw.value.forEach(async (item) => {
    try {
      const address = walletAddress.value.toString();
      if (address === undefined) throw new Error(`Wrong address ${address}`);

      if (item.type === 'moonpay') {
        const res = await getMoonPayLink(address.toString());
        if (!res || res.address !== address) return;
        item.url = res.url;
        item.canBeShowed = true;
      }

      if (item.type === 'transak') {
        item.canBeShowed = true;
      }

      if (item.type === 'coinbase') {
        const res = await getCoinbaseLink(address.toString());
        if (!res || res.address !== address) return;
        item.url = res.url;
        item.canBeShowed = true;
      }
    } catch (e) {
      item.url = '';
      item.canBeShowed = false;
    }
  });
};

/**
 * Watch connected wallet to fill buy APT menu
 */
watch(walletAddress, fillBuyAptMenuItems);
